import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'

import * as Sentry from '@sentry/react'

import { CareTeamChange, CareTeamChangeType } from './careTeamChanges'
import { baseApi, RTKQueryTag } from '../../../../../data/baseApi'
import { useAppDispatch } from '../../../../../data/store'
import { setToastContent } from '../../../../data/ltToastAutoActions'
import episodesApi, {
  useAssignCareTeamSlotsMutation,
  useEndCareTeamAssignmentMutation,
  useReassignCareTeamAssignmentMutation,
} from '../../../../episodes/data/episodesApi'

export const CARE_TEAM_UPDATED_TOAST_ID = 'care-team-updated-toast'
export const CARE_TEAM_UPDATE_FAILED_TOAST_ID = 'care-team-update-failed-toast'

export const useSaveCareTeam = (episodeId: string | undefined) => {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [isSaving, setIsSaving] = useState(false)
  const [assignCareTeamSlots] = useAssignCareTeamSlotsMutation()
  const [endCareTeamAssignment] = useEndCareTeamAssignmentMutation()
  const [reassignCareTeamAssignment] = useReassignCareTeamAssignmentMutation()

  const applyChange = useCallback(
    (targetEpisodeId: string, change: CareTeamChange) => {
      switch (change.type) {
        case CareTeamChangeType.END:
          return endCareTeamAssignment({
            episodeId: targetEpisodeId,
            careTeamSlotAssignmentId: change.careTeamSlotAssignmentId,
          }).unwrap()
        case CareTeamChangeType.REASSIGN:
          return reassignCareTeamAssignment({
            episodeId: targetEpisodeId,
            careTeamSlotAssignmentId: change.careTeamSlotAssignmentId,
            newProviderLyraId: change.newProviderLyraId,
          }).unwrap()
        case CareTeamChangeType.ASSIGN:
          return assignCareTeamSlots({
            episodeId: targetEpisodeId,
            providerLyraId: change.providerLyraId,
            careTeamSlots: change.careTeamSlots,
          }).unwrap()
      }
    },
    [assignCareTeamSlots, endCareTeamAssignment, reassignCareTeamAssignment],
  )

  const saveCareTeam = useCallback(
    async (changes: CareTeamChange[]): Promise<boolean> => {
      if (changes.length === 0) {
        return true
      }

      if (!episodeId) {
        return false
      }

      setIsSaving(true)
      try {
        for (const change of changes) {
          await applyChange(episodeId, change)
        }
      } catch (error) {
        Sentry.captureException(error)
        dispatch(
          setToastContent({
            text: intl.formatMessage({
              defaultMessage: "Couldn't update the care team. Please try again.",
              description: 'Error toast shown when saving changes to the neuropsych care team fails',
            }),
            id: CARE_TEAM_UPDATE_FAILED_TOAST_ID,
            toastType: 'error',
          }),
        )
        await dispatch(
          episodesApi.endpoints.getCareTeamSlots.initiate({ episodeId }, { forceRefetch: true, subscribe: false }),
        )
        return false
      } finally {
        dispatch(baseApi.util.invalidateTags([RTKQueryTag.EPISODE]))
        setIsSaving(false)
      }

      dispatch(
        setToastContent({
          text: intl.formatMessage({
            defaultMessage: 'Neuropsych care team updated',
            description: 'Success toast shown after a supervisor saves changes to the neuropsych care team',
          }),
          id: CARE_TEAM_UPDATED_TOAST_ID,
          toastType: 'success',
        }),
      )
      return true
    },
    [episodeId, applyChange, dispatch, intl],
  )

  return { saveCareTeam, isSaving }
}
